// Direct, in-browser COG streaming tuned for speed.
//
// Design choices that make tiles load as fast as possible:
//
//  1. ZERO REPROJECTION. The Vantor COGs are already EPSG:4326. The OpenLayers
//     map view is *also* EPSG:4326, and the tile grid below is built from the
//     COG's own pixel grid. So a screen tile maps 1:1 onto COG pixels — no
//     warping, no resampling, no reprojection on either CPU or GPU.
//
//  2. NATIVE OVERVIEW ALIGNMENT. We build one zoom level per IFD (full image +
//     every internal overview) at that overview's exact resolution, with the
//     tile size equal to the COG's internal block size (512). A requested tile
//     therefore lines up with stored 512×512 blocks → one decoded block per
//     tile, no cross-block reads.
//
//  3. TUNABLE WORKER POOL. geotiff.js decodes (LZW) in a pool of Web Workers.
//     The pool size is exposed to the UI so it can be tuned to the machine.
//
//  4. WebGL rendering of a 4-band uint8 DataTile → the RGBA texture is uploaded
//     straight to the GPU; band 4 (alpha) gives transparent COG borders.

import { fromUrl, Pool } from 'geotiff';
import DataTileSource from 'ol/source/DataTile.js';
import WebGLTileLayer from 'ol/layer/WebGLTile.js';
import TileGrid from 'ol/tilegrid/TileGrid.js';

const TILE = 512; // matches the COG internal block size

/**
 * Open a COG and build an OpenLayers WebGL tile layer that streams it directly.
 *
 * @param {string} url COG URL (EPSG:4326)
 * @param {object} opts
 * @param {number} [opts.workers] worker-pool size
 * @param {(s: {loading: number, total: number}) => void} [opts.onProgress]
 * @returns {Promise<{
 *   layer: import('ol/layer/WebGLTile.js').default,
 *   extent: number[],
 *   resolutions: number[],
 *   setWorkers: (n: number) => void,
 *   setOpacity: (o: number) => void,
 *   dispose: () => void,
 * }>}
 */
export async function createCogLayer(url, opts = {}) {
  const onProgress = opts.onProgress || (() => {});
  let pool = new Pool(clampWorkers(opts.workers));

  // Read the whole IFD chain once. geotiff fetches only headers here (ranged).
  const tiff = await fromUrl(url, { blockSize: 1 << 16, cacheSize: 512 });
  const count = await tiff.getImageCount();
  const images = [];
  for (let i = 0; i < count; i++) images.push(await tiff.getImage(i));

  // The full-resolution image (largest width) carries the authoritative
  // geo-extent; overviews share that extent at coarser pixel sizes.
  const full = images.reduce((a, b) => (b.getWidth() > a.getWidth() ? b : a));
  const [minX, minY, maxX, maxY] = full.getBoundingBox();
  const extent = [minX, minY, maxX, maxY];
  const spanX = maxX - minX;

  // A COG can interleave several IFD pyramids: the primary image + overviews,
  // and (for GDAL-style internal transparency masks) a parallel single-band
  // mask + its overviews at identical dimensions. We must build the tile
  // pyramid from ONLY the primary images — otherwise the duplicate widths
  // produce duplicate resolutions and OpenLayers rejects the tile grid.
  const primaryBands = full.getSamplesPerPixel();
  const dataImages = images
    .filter((im) => im.getSamplesPerPixel() === primaryBands)
    .sort((a, b) => a.getWidth() - b.getWidth());

  // Coarse → fine, strictly increasing width (defensive de-dup).
  const pyramid = [];
  for (const im of dataImages) {
    if (!pyramid.length || im.getWidth() > pyramid.at(-1).getWidth()) pyramid.push(im);
  }
  const resolutions = pyramid.map((im) => spanX / im.getWidth());

  // Internal transparency mask (single-band IFDs alongside multi-band data),
  // keyed by width so each pyramid level can find its matching mask level.
  const maskByWidth = new Map();
  if (primaryBands > 1) {
    for (const im of images) {
      if (im.getSamplesPerPixel() === 1) maskByWidth.set(im.getWidth(), im);
    }
  }
  const hasMask = maskByWidth.size > 0;

  // Texture band count: RGBA if the COG already has an alpha band OR we can
  // synthesize one from a mask; otherwise the native band count.
  const bandCount = primaryBands >= 4 ? 4 : primaryBands === 3 && hasMask ? 4 : primaryBands;
  const dataSamples = Array.from({ length: primaryBands }, (_, i) => i);
  const combineMask = primaryBands === 3 && hasMask;

  const tileGrid = new TileGrid({
    extent,
    origin: [minX, maxY], // top-left
    resolutions,
    tileSize: TILE,
  });

  let loading = 0;
  let total = 0;
  const tick = (d) => {
    loading += d;
    if (d > 0) total += 1;
    onProgress({ loading, total });
  };

  const source = new DataTileSource({
    projection: 'EPSG:4326',
    tileGrid,
    bandCount,
    wrapX: false,
    // Keep a generous client cache so panning back is instant.
    cacheSize: 1024,
    transition: 0,
    loader: async (z, x, y, { signal }) => {
      const image = pyramid[z];
      const w = image.getWidth();
      const h = image.getHeight();
      const px0 = x * TILE;
      const py0 = y * TILE;
      // Tiles entirely outside the image → fully transparent, no fetch.
      if (px0 >= w || py0 >= h) return new Uint8Array(TILE * TILE * bandCount);

      tick(1);
      const win = [px0, py0, px0 + TILE, py0 + TILE];
      try {
        if (combineMask) {
          // RGB image + separate mask IFD → assemble an RGBA tile so rotated
          // scenes get clean transparent borders instead of black corners.
          const mask = maskByWidth.get(w);
          const [rgb, m] = await Promise.all([
            image.readRasters({
              window: win, width: TILE, height: TILE,
              samples: [0, 1, 2], interleave: true, pool, fillValue: 0, signal,
            }),
            mask
              ? mask.readRasters({
                  window: win, width: TILE, height: TILE,
                  samples: [0], interleave: true, pool, fillValue: 0, signal,
                })
              : Promise.resolve(null),
          ]);
          const out = new Uint8Array(TILE * TILE * 4);
          for (let i = 0, j = 0, k = 0; i < TILE * TILE; i++, j += 3, k += 4) {
            out[k] = rgb[j];
            out[k + 1] = rgb[j + 1];
            out[k + 2] = rgb[j + 2];
            // Transparency masks are binary but may be stored as 0/1 or 0/255 —
            // treat any non-zero value as fully opaque.
            out[k + 3] = !m || m[i] ? 255 : 0;
          }
          return out;
        }
        const data = await image.readRasters({
          window: win,
          width: TILE,
          height: TILE,
          samples: dataSamples,
          interleave: true,
          pool,
          fillValue: 0, // 0 alpha → transparent outside footprint (RGBA COGs)
          signal,
        });
        return data instanceof Uint8Array ? data : Uint8Array.from(data);
      } finally {
        tick(-1);
      }
    },
  });

  const layer = new WebGLTileLayer({
    source,
    // A uint8 DataTile is sampled by the GPU as normalized [0,1] values, so the
    // band readings are used directly. Band 4 is the COG alpha → transparent
    // borders outside the footprint.
    style:
      bandCount >= 4
        ? {
            color: ['array', ['band', 1], ['band', 2], ['band', 3], ['band', 4]],
          }
        : {
            color: [
              'array',
              ['band', 1],
              ['band', Math.min(2, bandCount)],
              ['band', Math.min(3, bandCount)],
              1,
            ],
          },
  });

  function setWorkers(n) {
    const next = new Pool(clampWorkers(n));
    const old = pool;
    pool = next; // new reads use the new pool immediately
    source.refresh(); // re-request visible tiles through the new pool
    // Let any in-flight reads on the old pool settle, then free its workers.
    setTimeout(() => old.destroy?.(), 3000);
  }

  function setOpacity(o) {
    layer.setOpacity(o);
  }

  function dispose() {
    layer.getSource()?.clear?.();
    pool.destroy?.();
    tiff.close?.();
  }

  return { layer, extent, resolutions, setWorkers, setOpacity, dispose };
}

function clampWorkers(n) {
  const hw = navigator.hardwareConcurrency || 4;
  const fallback = Math.max(2, Math.min(hw, 8));
  const v = Number.isFinite(n) ? Math.round(n) : fallback;
  return Math.max(1, Math.min(16, v));
}

export const defaultWorkers = () => clampWorkers(undefined);
export const maxWorkers = 16;
